import {
  emptyResponseSchema,
  sessionsResponseSchema,
  userResponseSchema,
  type ChangePasswordInput,
  type LoginInput,
  type RegisterInput,
  type Session,
  type UpdateProfileInput,
  type User,
} from "@tm/contracts";
import { apiRequest, isApiError } from "./client";

export const authApi = {
  /** Current user, or `null` when not signed in. */
  async me(signal?: AbortSignal): Promise<User | null> {
    try {
      const { user } = await apiRequest("/auth/me", { schema: userResponseSchema, signal });
      return user;
    } catch (error: unknown) {
      if (isApiError(error, "UNAUTHORIZED")) return null;
      throw error;
    }
  },

  async register(input: RegisterInput): Promise<User> {
    const { user } = await apiRequest("/auth/register", {
      method: "POST",
      body: input,
      schema: userResponseSchema,
    });
    return user;
  },

  async login(input: LoginInput): Promise<User> {
    const { user } = await apiRequest("/auth/login", {
      method: "POST",
      body: input,
      schema: userResponseSchema,
    });
    return user;
  },

  async logout(): Promise<void> {
    await apiRequest("/auth/logout", { method: "POST", schema: emptyResponseSchema });
  },

  async updateProfile(input: UpdateProfileInput): Promise<User> {
    const { user } = await apiRequest("/auth/me", {
      method: "PATCH",
      body: input,
      schema: userResponseSchema,
    });
    return user;
  },

  async changePassword(input: ChangePasswordInput): Promise<void> {
    await apiRequest("/auth/change-password", {
      method: "POST",
      body: input,
      schema: emptyResponseSchema,
    });
  },

  async verifyEmail(token: string): Promise<User> {
    const { user } = await apiRequest("/auth/verify-email", {
      method: "POST",
      body: { token },
      schema: userResponseSchema,
    });
    return user;
  },

  async resendVerification(): Promise<void> {
    await apiRequest("/auth/resend-verification", { method: "POST", schema: emptyResponseSchema });
  },

  async forgotPassword(email: string): Promise<void> {
    await apiRequest("/auth/forgot-password", {
      method: "POST",
      body: { email },
      schema: emptyResponseSchema,
    });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await apiRequest("/auth/reset-password", {
      method: "POST",
      body: { token, password },
      schema: emptyResponseSchema,
    });
  },

  async listSessions(signal?: AbortSignal): Promise<Session[]> {
    const { sessions } = await apiRequest("/auth/sessions", {
      schema: sessionsResponseSchema,
      signal,
    });
    return sessions;
  },

  async revokeSession(sessionId: string): Promise<void> {
    await apiRequest(`/auth/sessions/${encodeURIComponent(sessionId)}`, {
      method: "DELETE",
      schema: emptyResponseSchema,
    });
  },
};
